import { isEmpty, isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { MBTextInput } from '@shared/components/textInput';
import { EmailField } from '@shared/data/entity/fields/EmailField';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { getComponentPlaceholder } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { EditFieldContainer } from './EditFieldContainer';
import { LookupEditFieldContainerProps } from './EditFieldContainerProps';
import { useFieldDraft } from './useFieldDraft';

interface Props extends LookupEditFieldContainerProps {
  field: EmailField | null;
  component: API.EmailComponentFragment | API.RelatedCardLookupComponent;
  updateField?: (componentId: string, field: EmailField | null) => void;
  allowClear?: boolean;
}

export function EmailEditField({
  field,
  component,
  updateField,
  error,
  allowClear,
  showIcon,
  label,
  showLabel = true,
  header,
  footer,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: Props) {
  // The field is the backing state. `draft` only holds in-progress text (trailing commas and
  // spaces) that wouldn't survive a round-trip through `EmailField`, and yields whenever the
  // field changes from outside this input — an async load, the card modal swapping entities,
  // or another user's edit accepted from the conflict banner.
  const fieldValue = field?.emails.join(', ') ?? '';
  const [draft, commitDraft] = useFieldDraft<string, string>(fieldValue);
  const value = draft ?? fieldValue;
  const [isFocused, onFocus, onBlur] = useBoolean(false);
  const { setComponentFocused, setComponentBlurred } = useComponentFocus();

  // Derive synchronously from `value` so it stays in lockstep with the input. When this
  // trailed `value` via a useEffect, the 0→1 character transition left `hasInput` false
  // for one frame, which flashed the error through the `!hasInput` gate below.
  const hasInput = !isEmpty(getEmailsFromInput(value));
  const showLocalError = !isFocused;
  // Surface email *formatting* errors on blur, independent of the form-level `error`
  // prop (which only includes local validation after a submit attempt). Required-field
  // errors still come solely from `error` and remain submit-gated.
  const formatError = useMemo(() => {
    const emails = getEmailsFromInput(value);
    if (isEmpty(emails)) {
      return undefined;
    }
    return new EmailField(emails).validate() ?? undefined;
  }, [value]);
  const displayError = error ?? formatError;
  const showError = (!hasInput || showLocalError) && !isEmpty(displayError);

  const handleFocus = useCallback(() => {
    onFocus();
    setComponentFocused();
  }, [setComponentFocused, onFocus]);

  const handleBlur = useCallback(() => {
    onBlur();
    setComponentBlurred();
  }, [setComponentBlurred, onBlur]);

  const handleChangeText = useCallback(
    (text: string) => {
      const emails = getEmailsFromInput(text);
      commitDraft(text, emails.join(', '));
      updateField?.(component.id, !isEmpty(emails) ? new EmailField(emails) : null);
    },
    [updateField, component.id, commitDraft],
  );

  const onClear = useCallback(() => {
    commitDraft('', '');
    updateField?.(component.id, null);
  }, [updateField, component.id, commitDraft]);

  const hasValue = !isEmpty(value);
  const isReadOnly = component.readonly || isNil(updateField);
  const showClearButton = hasValue && !isReadOnly && allowClear;

  return (
    <EditFieldContainer
      component={component}
      showIcon={showIcon}
      label={label}
      showLabel={showLabel}
      showClearButton={showClearButton}
      onClear={onClear}
      error={showError ? displayError : undefined}
      header={header}
      footer={footer}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      isReadOnly={isReadOnly}
      style={style}
      printContent={field?.emails.join(', ') || undefined}
    >
      <MBTextInput
        noBorder
        noPadding
        inputMode="email"
        textContentType="emailAddress"
        keyboardType="email-address"
        value={value}
        onChangeText={handleChangeText}
        placeholder={getComponentPlaceholder(component)}
        onBlur={handleBlur}
        onFocus={handleFocus}
        editable={!isReadOnly}
        style={{ paddingVertical: isEmpty(value) ? grid(1.25) : undefined }}
      />
    </EditFieldContainer>
  );
}

function getEmailsFromInput(input: string) {
  return input
    .replace(/( )?,( )?/g, ',')
    .trim()
    .split(',')
    .filter(Boolean);
}
